import React, { useRef, useState } from 'react'
import { GrieflingsCard, Header, Stages } from 'components'
import { Link } from 'react-router-dom'
import { useMutation } from 'react-query'
import { messageWOG } from 'api/api'
import { toast as toastify } from 'react-toastify'

import QuoteLeft from 'assets/quote-left'
import QuoteRight from 'assets/quote-right'
import LinkedIn from 'assets/linkedin'
import Instagram from 'assets/instagram'

import {
  IntroVid,
  throughShadowLogo,
} from 'assets'
import {
  backgroundHead,
  acceptanceHead,
  bargainingHead,
  denialHead,
  depressionHead,
  angerHead,
  acceptance,
  bargaining,
  denial,
  depression,
  anger,
  journeyBG,
} from 'assets/compressed'

const Home = () => {
  // START JOURNEY CTA
  const aboutUsRef = useRef(null)
  const handleStartJourney = () => {
    if (aboutUsRef.current) {
      aboutUsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // ACTIVE CARD
  const [isActive, setIsActive] = useState('')
  const handleActive = (name) => {
    setIsActive(prev => prev === name ? '' : name)
  }

  // RENDER CARDS V1
  const grieflingsList = [
    { img: denialHead, activeImg: denial, name: 'denial', color: '#AAB9C5' },
    { img: angerHead, activeImg: anger, name: 'anger', color: '#FF7271' },
    { img: bargainingHead, activeImg: bargaining, name: 'bargaining', color: '#EFC89A' },
    { img: depressionHead, activeImg: depression, name: 'depression', color: '#5E7AC0' },
    { img: acceptanceHead, activeImg: acceptance, name: 'acceptance', color: '#78D9B1' },
  ]
  const renderGrieflings = () => {
    return grieflingsList.map((griefling, index) => {
      return (
        <div
          key={index}
          onClick={() => handleActive(griefling.name)}
          style={{
            cursor: 'pointer',
            backgroundColor: griefling.color,
            borderRadius: '1rem',
            flex: 1,
            overflow: 'hidden',
            height: isActive !== griefling.name ? '100%' : 'fit-content',
          }}
        >
          <img 
            alt={griefling.name}
            src={isActive === griefling.name ? griefling.activeImg : griefling.img}
            style={{ 
              width: '100%',
              objectFit: 'cover',
              height: isActive === griefling.name ? 'initial' : '20.5rem',
              padding: isActive === griefling.name ? '1rem' : '0',
            }} 
          />
          {isActive === griefling.name && (
            <div style={{ padding: '1rem', fontFamily: 'MerriWeather', fontSize: '1.25rem', textAlign: 'center' }}>
              {griefling.name.charAt(0).toUpperCase() + griefling.name.slice(1)}
            </div>
          )}
        </div>
      )
    })
  }

  // RENDER CARDS V2
  const [selectedCharacter, setSelectedCharacter] = useState(null)
  const characters = [
    {
      id: "char1",
      name: "Hi! I'm Dan",
      description: "I cherish everything I've learned and experienced, all kept in my favorite backpack, which I always carry. No others <br/> —just me and my backpack! <div style='margin-top:0.5rem;'>🙂 Oh, fire? No worries, I'm good!</div>",
      backgroundColor: "#AAB9C5",
      image: denialHead,
      alternateImage: denial,
    },
    {
      id: "char2",
      name: "I'm Fizz! <br/> Fizz the Fury!",
      description: "I’m your buried anger! You think you can greet me with a smile and pretend everything’s fine?! <div style='margin-top:0.5rem;'>I’ll be here to remind you how messed up this world is!</div>",
      backgroundColor: "#FF7271",
      image: angerHead,
      alternateImage: anger,
    },
    {
      id: "char3",
      name: "I'm Benson. <br/> The Bargainer.",
      description: "If you’ve got the time, I can help! Want me to rewind everything? We can start from any point. <div style='margin-top:0.5rem;'>Of course. Everything is negotiable with my compass.</div>",
      backgroundColor: "#EFC89A",
      image: bargainingHead,
      alternateImage: bargaining,
    },
    {
      id: "char4",
      name: "“It’s pointless... nothing will ever change…”",
      description: "Drew eyes is blank. Its breathing was heavy, each exhale carrying the weight of the world. It’s massive, filling all the space, chained to the ground. It wasn’t punished. It locked itself.",
      backgroundColor: "#5E7AC0",
      image: depressionHead,
      alternateImage: depression,
    },
    {
      id: "char5",
      name: "Hi there! I'm Grace.",
      description: "Even though I might not have blossomed perfectly, I’ve accepted it’s as part of me. <div style='margin-top:0.5rem;'>I'm proud of the person I've become, and I'll keep learning and growing bit by bit every day.</div>",
      backgroundColor: "#78D9B1",
      image: acceptanceHead,
      alternateImage: acceptance,
    },
  ]

  // FORM
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    message: '',
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const { mutate, isLoading } = useMutation({
    mutationFn: (payload) => messageWOG(payload),
    onSuccess: (response) => {
      if (response.code === 201) {
        toastify.success("Submission submitted successfully!")
      } else if (response.code === 400) {
        toastify.warn("Email and/or phone number already exists")
      } else if (response.code === 500) {
        toastify.warn("An error occurred while registering user")
      }
    },
    onError: (err) => {
      const errMessage = err.response.data.message
      toastify.error(errMessage)
    },
  })

  const isFormEmpty = Object.values(formData).some(value => value.trim() === '')

  const handleSubmit = (e) => {
    e.preventDefault()
    mutate(formData)
  }

  // FORM STYLING
  const labelStyle = {
    display: 'block',
    marginBottom: '1rem',
    fontFamily: 'MerriWeather-Regular',
    fontWeight: 400,
    color: '#000',
    fontSize: '0.85rem',
  }
  const inputStyle = {
    display: 'block',
    width: '100%',
    padding: '0.6rem 0.8rem',
    marginTop: '0.4rem',
    fontSize: '1rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'MerriWeather-Regular',
    fontSize: '0.85rem',
  }

  return (
    <div>
      <Header />

      {/* INTRO */}
      <div style={{
        position: 'relative',
        // width: '100vw',
        // height: '90vh',
        width: '100%',
        padding: '12.5rem 0',
        display: 'flex',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: '-8.5px',
          left: '-8.5px',
          right: '-8.5px',
          bottom: '-8.5px',
          backgroundImage: `url(${backgroundHead})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          filter: 'blur(4px)',
          zIndex: -1,
        }} />
        <div style={{
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          rowGap: '3.5rem',
        }}>
          <div style={{ fontFamily: 'TanHeadline', fontSize: '5rem', textAlign: 'center' }}>
            <span style={{ color: '#F5F5F5' }}>Welcome to the</span> <br />
            <span style={{ color: '#FFBD59' }}>World of Grieflings!</span>
          </div>
          <button
            onClick={handleStartJourney}
            style={{
              cursor: 'pointer',
              fontFamily: 'MerriWeather',
              fontSize: '1.65rem',
              fontWeight: 'bold',
              backgroundColor: 'white',
              padding: '1rem 2rem',
              borderRadius: '1rem',
              border: 'none',
            }}
          >
            Start Your Journey
          </button>
        </div>
      </div>

      {/* ABOUT */}
      <div
        ref={aboutUsRef}
        style={{
          padding: '10rem 20rem 10rem 20rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          rowGap: '2rem',
        }}
      >
        <div style={{ fontFamily: 'TanHeadline', fontSize: '2.25rem', color: '#FFBD59', textAlign: 'center' }}>
          Meet the Grieflings!
        </div>
        <div style={{ fontFamily: 'MerriWeather', fontSize: '1.25rem', textAlign: 'center', lineHeight: '1.75' }}>
          These playful little spirit figures are here to guide you through the ups and downs of life. Each Griefling represents a stage of grief, making them perfect companions on your emotional journey.
        </div>
        <div style={{ fontFamily: 'MerriWeather', fontSize: '1.25rem', textAlign: 'center', lineHeight: '1.75', margin: '0rem 7.5rem' }}>
          The Grieflings remind us that it's okay to feel, heal, and embrace every part of our story!
        </div>
      </div>

      {/* VIDEO */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <video
          autoPlay
          loop
          muted
          // style={{ width: '100vw', height: '90vh' }}
          style={{ width: '80%', borderRadius: '1.5rem' }}
        >
          <source src={IntroVid} />
        </video>
      </div>

      {/* CAROUSEL V1 */}
      {/* <div style={{
        backgroundColor: '#F4F7FA',
        padding: '2.5rem',
        margin: '0 4rem 15rem 4rem',
        borderRadius: '2.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        columnGap: '2rem',
        boxShadow: '0px 0px 20px 8px rgba(130,130,130,0.2)',
      }}>
        {renderGrieflings()}
      </div> */}

      {/* CAROUSEL V2 */}
      <div style={{
        backgroundColor: '#F4F7FA',
        padding: '2.5rem',
        margin: '13.5rem 4rem 15rem 4rem',
        borderRadius: '2.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        columnGap: '1.5rem',
        boxShadow: '0px 0px 20px 8px rgba(130,130,130,0.2)',
      }}>
        {characters.map((character) => (
          <GrieflingsCard
            key={character.id}
            character={character.id}
            data={character}
            isSelected={selectedCharacter === character.id}
            onSelect={() => {
              setSelectedCharacter(selectedCharacter === character.id ? null : character.id)
            }}
          />
        ))}
      </div>

      {/* JOURNEY */}
      <div style={{
        position: 'relative',
        // width: '100vw',
        width: '100%',
        display: 'flex',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: '-8.5px',
          left: '-8.5px',
          right: '-8.5px',
          bottom: '-8.5px',
          backgroundImage: `url(${journeyBG})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          filter: 'blur(4px)',
          zIndex: -1,
        }} />
        <div style={{
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          rowGap: '3.5rem',
          padding: '5rem',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            rowGap: '1.75rem',
            marginBottom: '2rem',
          }}>
            <div style={{ fontFamily: 'TanHeadline', fontSize: '3rem', color: '#FFBD59', textAlign: 'center' }}>
              Embrace Your Journey
            </div>
            <div style={{ fontFamily: 'MerriWeather', fontSize: '1.5rem', color: 'white', textAlign: 'center', margin: '0 7.5rem' }}>
              {/* Every emotions matters. What you feel, matters. Grieving is a courageous journey that requires us to process, heal, and move forward. It’s never a straight road; each twist and turn brings its own challenges.
              <br /><br />
              Let the Grieflings be your companions in your journey like the roadmap below */}
              Grieving is a process or journey that is not a straight line. Sometimes during the way, we might felt lost. It’s time to ask ourselves, where am I right now?
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Stages />
            {/* <img
              alt='grieflings-roadmap'
              src={grieflingsRoadmap}
              style={{ width: '100%', objectFit: 'cover', padding: '0 10rem' }}
            /> */}
          </div>
        </div>
      </div>

      {/* QUOTES */}
      <div style={{ padding: '10rem 0 10rem 0' }}>
        <div style={{
          padding: '0 17.5rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          rowGap: '4rem',
        }}>
          <QuoteLeft />
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: '2rem' }}>
            <div style={{ fontFamily: 'MerriWeather', fontSize: '2rem', fontWeight: '500', lineHeight: '1.5', textAlign: 'center' }}>
              "What if I made figures for each stage of grief?"
            </div>
            <div style={{ fontFamily: 'MerriWeather', fontSize: '1.5rem', fontWeight: 'lighter', lineHeight: '1.5', textAlign: 'center' }}>
              That was the question one of my students asked during a brainstorming session. As a psychologist, I’m used to exploring grief through emotional expression, cognitive understanding, and relational support—but this question invited me to approach it differently. It made me wonder: What would grief look like if we could see it? {' '}
              <Link to='/session-discussion'>
                Click here to read more
              </Link>
            </div>
            <div style={{ fontFamily: 'MerriWeather', fontSize: '1.125rem', fontWeight: '500', lineHeight: '1.5', textAlign: 'center', marginTop: '1rem' }}>
              Fadhilah Eryananda, M.Psi., Psikolog <br/>
              Clinical Psychologist | Specialist in Trauma, Grief, and Relational Healing
            </div>
          </div>
          <QuoteRight />
        </div>
        {/* <div style={{
          marginTop: '7rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          rowGap: '0.25rem',
        }}>
          <img
            alt='author'
            src={authorImg}
            style={{ borderRadius: '50%', width: '5rem', marginBottom: '0.85rem' }}
          />
          <div style={{ fontFamily: 'MerriWeather', fontSize: '1.125rem', textAlign: 'center' }}>
            <b>Gabrielle C.</b>
          </div>
          <div style={{ fontFamily: 'MerriWeather', fontSize: '1.125rem', fontWeight: 'lighter', color: '#797676', textAlign: 'center' }}>
            Creator of Grieflings
          </div>
        </div> */}
        <div style={{
          marginTop: '10rem',
          padding: '0 15rem',
          display: 'flex',
          alignItems: 'center',
          columnGap: '5rem',
        }}>
          <div>
            <img alt='through-shadow' src={throughShadowLogo} style={{ width: '25rem' }} />
          </div>
          <div style={{ fontFamily: 'MerriWeather-Light', fontSize: '1.5rem', lineHeight: '1.65', textAlign: 'justify' }}>
            <b>The Grieflings are a key part of the "Through Shadow" project,</b>
            <br/>
            crafted to help you explore the vibrant spectrum of emotions we all experience.
          </div>
        </div>
      </div>

      {/* FOLLOW */}
      <div style={{
        backgroundColor: '#FFBD59',
        borderRadius: '1.5rem',
        margin: '0 3rem 3rem 3rem',
        padding: '5rem',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', rowGap: '2rem' }}>
          <div style={{ fontFamily: 'MerriWeather-Regular', fontSize: '1.5rem', lineHeight: '1.65', textAlign: 'center', maxWidth: '60%' }}>
            Whether through journaling, art, or conversation, your voice matter, your journey matters— not just to you, but to others who walk similar paths.
            <br /><br />
            Follow and share your story with us @throughshadow on Instagram to stay connected!
          </div>
          <Link to='https://www.instagram.com/throughshadow' target='__blank'>
            <button style={{
              fontFamily: 'MerriWeather-Regular',
              fontSize: '1.75rem',
              padding: '0.5rem 1rem',
              border: 'none',
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              cursor: 'pointer',
            }}>
              Follow
            </button>
          </Link>
        </div>
      </div>

      {/* ABOUT ME */}
      <div style={{
        marginTop: '7.5rem',
        padding: '5rem 10rem',
      }}>
        <div style={{ fontFamily: 'MerriWeather-SemiBold', fontSize: '2rem', marginBottom: '2.5rem' }}>
          About The Creator
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '3fr 4fr',
          alignItems: 'center',
          columnGap: '3.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img alt='shelf' src='/shelf.png' style={{ width: '100%', borderRadius: '0.75rem' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: '1.5rem' }}>
            {/* INTRO SECTION */}
            <div style={{ fontFamily: 'MerriWeather-Medium', fontSize: '1.5rem', lineHeight: '1.5' }}>
              Gabrielle Clarisa, BBA <br />
              Graphic Designer | 3D Animation Student | Translator
            </div>
            <div style={{ fontFamily: 'MerriWeather-Light', fontSize: '1.25rem', lineHeight: '1.5' }}>
              A curious soul wandering this world, eager to experience all it has to offer. Love everything about art, music, and the stories behind something and it process. Originally a tourism management student, I love traveling and diving into new cultures and languages.
            </div>
            <div style={{ fontFamily: 'MerriWeather-Light', fontSize: '1.25rem', lineHeight: '1.5' }}>
              When I’m not at my desk, you’ll probably find me catching up on sleep (sleep deprivation is real!), at the gym, indulging in sushi or desserts, or gaming my heart out.
            </div>
            <div style={{ fontFamily: 'MerriWeather-Regular', fontSize: '1.25rem', lineHeight: '1.5' }}>
              Let’s Connect!
            </div>
            {/* SOCIALS SECTION */}
            <div style={{ display: 'flex', alignItems: 'flex-end', columnGap: '5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', columnGap: '0.75rem' }}>
                <LinkedIn size='40' />
                <Link to='https://www.linkedin.com/in/gabrielle-clarisa/' target='__blank'>
                  <div style={{ fontFamily: 'MerriWeather-Light', fontSize: '1.35rem' }}>
                    Gabrielle Clarisa
                  </div>
                </Link>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', columnGap: '0.75rem' }}>
                <Instagram size='40' />
                <Link to='https://www.instagram.com/hazy_hazly/' target='__blank'>
                  <div style={{ fontFamily: 'MerriWeather-Light', fontSize: '1.35rem' }}>
                    Hazy_Hazly
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LEARN MORE */}
      <div style={{
        marginTop: '1.5rem',
        padding: '5rem 10rem',
      }}>
        <div style={{ fontFamily: 'MerriWeather-SemiBold', fontSize: '2rem', textAlign: 'center', marginBottom: '3.5rem' }}>
          Learn More
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            columnGap: '5rem',
          }}
        >
          {/* SADARI */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#333333',
              borderRadius: '1rem',
              fontFamily: 'MerriWeather-Light',
              height: '450px',
              overflow: 'hidden',
            }}
          >
            <div style={{ height: '60%', position: 'relative' }}>
              <img
                alt="sadari"
                src="/sadari.png"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '1rem 1rem 0 0',
                }}
              />
            </div>
            <div
              style={{
                height: '40%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '1.25rem',
                color: 'white',
              }}
            >
              <Link
                to="https://www.instagram.com/sadari_diri/"
                target="_blank"
                style={{
                  color: 'white',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '1.25rem', lineHeight: '1.5', marginBottom: '1rem' }}>
                  Sadari Diri Psychology and Counseling Service
                </div>
              </Link>
              <Link
                to="https://www.instagram.com/sadari_diri/"
                target="_blank"
                style={{ color: 'white', textDecoration: 'none', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'MerriWeather-Medium', fontSize: '1.25rem' }}>Read more</div>
                  <div style={{ fontSize: '1.5rem' }}>→</div>
                </div>
              </Link>
            </div>
          </div>

          {/* FOREST SHADOWS */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#333333',
              borderRadius: '1rem',
              fontFamily: 'MerriWeather-Light',
              height: '450px',
              overflow: 'hidden',
            }}
          >
            <div style={{ height: '60%', position: 'relative' }}>
              <img
                alt="forest-shadows"
                src="/forest-shadows2.webp"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '1rem 1rem 0 0',
                }}
              />
            </div>
            <div
              style={{
                height: '40%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '1.25rem',
                color: 'white',
              }}
            >
              <Link
                to="https://youtu.be/LkBvvsLMiDc?si=YaxZC3GWWNtDdFY_"
                target="_blank"
                style={{
                  color: 'white',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '1.25rem', lineHeight: '1.5', marginBottom: '1rem' }}>
                  Forest of 5 Shadows
                </div>
              </Link>
              <Link
                to="https://youtu.be/LkBvvsLMiDc?si=YaxZC3GWWNtDdFY_"
                target="_blank"
                style={{ color: 'white', textDecoration: 'none', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'MerriWeather-Medium', fontSize: '1.25rem' }}>Read more</div>
                  <div style={{ fontSize: '1.5rem' }}>→</div>
                </div>
              </Link>
            </div>
          </div>

          {/* BOOK COPY */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#333333',
              borderRadius: '1rem',
              fontFamily: 'MerriWeather-Light',
              height: '450px',
              overflow: 'hidden',
            }}
          >
            <div style={{ height: '60%', position: 'relative' }}>
              <img
                alt="book-copy"
                src="/book-copy2.webp"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '1rem 1rem 0 0',
                }}
              />
            </div>
            <div
              style={{
                height: '40%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '1.25rem',
                color: 'white',
              }}
            >
              <Link
                to="https://www.behance.net/gallery/227716977/The-Grieflings-Concept-Design"
                target="_blank"
                style={{
                  color: 'white',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '1.25rem', lineHeight: '1.5', marginBottom: '1rem' }}>
                  Get to know the grieflings!
                </div>
              </Link>
              <Link
                to="https://www.behance.net/gallery/227716977/The-Grieflings-Concept-Design"
                target="_blank"
                style={{ color: 'white', textDecoration: 'none', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'MerriWeather-Medium', fontSize: '1.25rem' }}>Read more</div>
                  <div style={{ fontSize: '1.5rem' }}>→</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* FORM */}
      <div style={{ paddingBottom: '0.5rem' }}>
        <div style={{
          borderRadius: '1.5rem',
          margin: '4rem 10rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
        }}>
          <div>
            <img alt='woman-laptop' src='/book-copy3.webp' style={{ width: '100%', borderRadius: '1.5rem 0 0 1.5rem', height: '100%', objectFit: 'cover' }} />
          </div>
          <div>
            <form
              onSubmit={handleSubmit}
              style={{
                maxWidth: '100%',
                padding: '2.5rem',
                backgroundColor: '#FFBD59',
                borderRadius: '0 1.5rem 1.5rem 0',
                fontFamily: 'Segoe UI, sans-serif',
                height: '100%',
                objectFit: 'cover',
              }}
            >
              <h2 style={{ fontFamily: 'MerriWeather-Regular', textAlign: 'center', marginBottom: '1.5rem' }}>
                Let's Keep in Touch!
              </h2>
              <label style={labelStyle}>
                Full Name
                <input
                  type="text"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              </label>
              <label style={labelStyle}>
                Phone Number
                <input
                  type="number"
                  name="phoneNumber"
                  placeholder="Enter your phone number"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              </label>
              <label style={labelStyle}>
                Email
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  style={inputStyle}
                  required
                />
              </label>
              <label style={labelStyle}>
                Message
                <textarea
                  name="message"
                  placeholder="Write your message here"
                  value={formData.message}
                  onChange={handleChange}
                  style={{ ...inputStyle, height: '100px', resize: 'vertical' }}
                  required
                />
              </label>
              <button
                type="submit"
                disabled={isLoading || isFormEmpty}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginTop: '1rem',
                  backgroundColor: isLoading || isFormEmpty ? '#BFBFBF' : '#333333',
                  color: isLoading || isFormEmpty ? '#636363' : 'white',
                  border: 'none',
                  borderRadius: '0.65rem',
                  fontSize: '1rem',
                  cursor: isLoading ? 'wait' : isFormEmpty ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s ease',
                  fontFamily: 'MerriWeather-Regular',
                }}
              >
                {isLoading ? 'Loading...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </div>

    </div>
  )
}

export default Home
